import { DialogTrigger, Dialog, Modal } from 'react-aria-components';
import { useForm, SubmitHandler } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { useAtom } from 'jotai';
import * as yup from 'yup';

import { createOffice } from '@/api/createOffice';
import { updateOffice } from '@/api/updateOffice';
import Button from '@/components/Button/Button';
import InputText from '@/components/InputText/InputText';
import SelectField from '@/components/SelectField/SelectField';
import { PREFECTURE_OPTIONS } from '@/const/prefecture';
import { NotificationAtom } from '@/jotai/jotai';
import { Office, CreateOfficeRequest } from '@/types/office';

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  afterSubmit: () => void;
  office?: Office;
};

export const CreateOrEditOfficeDialog = ({ isOpen, setIsOpen, afterSubmit, office }: Props) => {
  const isEdit = office !== undefined;

  const [_, setNotification] = useAtom(NotificationAtom);

  const schema: yup.ObjectSchema<CreateOfficeRequest> = yup
    .object({
      name: yup.string().required('事業者名は必須です'),
      officeNumber: yup.string().required('事業者番号は必須です'),
      representative: yup.string().required('代表者名は必須です'),
      postalCode: yup.string().required('郵便番号は必須です'),
      prefectureId: yup.number().required('都道府県は必須です'),
      municipality: yup.string().required('市区町村は必須です'),
      address1: yup.string().required('住所1は必須です'),
      address2: yup.string(), // オプショナル
      phoneNumber: yup.string().required('事業所電話番号は必須です'),
    })
    .required();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CreateOfficeRequest>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: office ? office.name : '',
      officeNumber: office ? office.officeNumber : '',
      representative: office ? office.representative : '',
      postalCode: office ? office.postalCode : '',
      prefectureId: office ? office.prefectureId : 0,
      municipality: office ? office.municipality : '',
      address1: office ? office.address1 : '',
      address2: office ? office.address2 : '',
      phoneNumber: office ? office.phoneNumber : '',
    },
  });

  const onSubmit: SubmitHandler<CreateOfficeRequest> = async (data) => {
    if (!isEdit) {
      try {
        const result = await createOffice({
          name: data.name,
          officeNumber: data.officeNumber,
          representative: data.representative,
          postalCode: data.postalCode,
          prefectureId: data.prefectureId,
          municipality: data.municipality,
          address1: data.address1,
          address2: data.address2 || '',
          phoneNumber: data.phoneNumber,
        });
        if (result.status === 201) {
          setNotification({
            headingLevel: 'h2',
            type: 'success',
            bannerStyle: 'standard',
            title: '事業所マスタを登録しました',
            message: '事業所マスタを登録しました',
          });
        } else {
          setNotification({
            headingLevel: 'h2',
            type: 'error',
            bannerStyle: 'standard',
            title: '事業所マスタの登録に失敗しました',
            message: '事業所マスタの登録に失敗しました',
          });
        }
      } catch (error) {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '事業所マスタの登録に失敗しました',
          message: '事業所マスタの登録に失敗しました',
        });
      }
    } else {
      try {
        const result = await updateOffice(office!.id, {
          id: office!.id,
          name: data.name,
          officeNumber: data.officeNumber,
          representative: data.representative,
          postalCode: data.postalCode,
          prefectureId: data.prefectureId,
          municipality: data.municipality,
          address1: data.address1,
          address2: data.address2 || '',
          phoneNumber: data.phoneNumber,
        });
        if (result.status === 200) {
          setNotification({
            headingLevel: 'h2',
            type: 'success',
            bannerStyle: 'standard',
            title: '事業所マスタを更新しました',
            message: '事業所マスタを更新しました',
          });
        } else {
          setNotification({
            headingLevel: 'h2',
            type: 'error',
            bannerStyle: 'standard',
            title: '事業所マスタの更新に失敗しました',
            message: '事業所マスタの更新に失敗しました',
          });
        }
      } catch (error) {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '事業所マスタの更新に失敗しました',
          message: '事業所マスタの更新に失敗しました',
        });
      }
    }

    setIsOpen(false);
    afterSubmit();
  };

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={() => setIsOpen(false)}
    >
      <Modal className="fixed inset-0 z-50 flex h-full w-full items-center justify-center bg-black/50">
        <Dialog className="w-[900px] rounded-lg border border-gray-400 bg-white p-6 shadow-lg outline-none">
          <div className="flex flex-row justify-between">
            <h1 className="mb-5 text-2xl font-700">
              {isEdit ? '事業所マスタ更新' : '事業所マスタ登録'}
            </h1>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="w-full p-10"
          >
            <div className="flex justify-between space-x-2 p-4">
              <InputText
                label="事業者番号"
                type="text"
                required={true}
                {...register('officeNumber')}
              />
              <InputText
                label="事業者名"
                type="text"
                required={true}
                {...register('name')}
              />
              <InputText
                label="代表者名"
                type="text"
                required={true}
                {...register('representative')}
              />
            </div>
            <div className="flex justify-between space-x-2 p-4">
              <InputText
                label="郵便番号"
                type="text"
                required={true}
                {...register('postalCode')}
              />
              <SelectField
                name="prefectureId"
                label="都道府県"
                control={control}
                items={PREFECTURE_OPTIONS.map((prefecture) => ({
                  label: prefecture.name,
                  value: prefecture.id,
                }))}
                required={true}
              />
              <InputText
                label="市区町村"
                type="text"
                required={true}
                {...register('municipality')}
              />
            </div>
            <div className="flex justify-between space-x-2 p-4">
              <InputText
                label="住所1"
                type="text"
                required={true}
                {...register('address1')}
              />
              <InputText
                label="住所2"
                type="text"
                {...register('address2')}
              />
              <InputText
                label="事業所電話番号"
                type="text"
                required={true}
                {...register('phoneNumber')}
              />
            </div>
            <div className="mt-10 flex flex-row items-center justify-center space-x-2">
              <Button
                type="submit"
                size="lg"
                variant="outline"
                onClick={() => setIsOpen(false)}
              >
                戻る
              </Button>
              <Button
                type="submit"
                size="lg"
                variant={Object.keys(errors).length > 0 ? 'disabled' : 'solid-fill'}
                disabled={Object.keys(errors).length > 0}
              >
                {isEdit ? '保存' : '登録'}
              </Button>
            </div>
          </form>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
};
